import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { canView, getUserType } from "../permissions";
import API_BASE from "../config";

const DashboardHome = () => {
  const navigate = useNavigate();
  const userType = getUserType();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingCounts, setBookingCounts] = useState({});

  useEffect(() => {
    // Get user id from session
    let userId = "1";
    try {
      const session = JSON.parse(localStorage.getItem("user_session"));
      if (session && session.id) userId = session.id;
    } catch (e) { }

    fetch(`${API_BASE}/api/getoverduetask`, {
      method: "POST",
      headers: { Authorization: "1", "Content-Type": "application/json" },
      body: JSON.stringify({ table: "task", user_id: userId }),
    })
      .then((r) => r.json())
      .then((json) => {
        if (json.status == true && json.data) setStats(json.data);
      })
      .catch(() => { });

    // Fetch bookings for status counts
    fetch(`${API_BASE}/api/allrows`, {
      method: "POST",
      headers: { Authorization: "1", "Content-Type": "application/json" },
      body: JSON.stringify({ table: "bookings", token: "3232" }),
    })
      .then((r) => r.json())
      .then((json) => {
        setLoading(false);
        if (json.status == true && Array.isArray(json.data)) {
          const counts = {};
          json.data.forEach(b => {
            let st = b.status || "";
            // If status is empty, maybe try to map from update_status or r_status
            if (!st && b.update_status === "Done") st = "Registry Done";
            if (!st && b.r_status === "Done") st = "Registry Done";
            if (!st) st = "Booked"; // Default fallback if totally empty
            
            counts[st] = (counts[st] || 0) + 1;
          });
          setBookingCounts(counts);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  const s = stats || {};

  const statCards = [
    { label: "Total Bookings", value: s.bookin ?? "—", icon: "🏗️", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", change: `${s.cancelbookings ?? 0} cancelled`, link: canView(userType, "bookings") ? "/dashboard/bookings" : null },
    { label: "Total Payments", value: s.payments ?? "—", icon: "💵", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", change: `${s.cpayment ?? 0} confirmed`, link: canView(userType, "payments") ? "/dashboard/payments" : null },
    { label: "Available Units", value: s.units ?? "—", icon: "🏠", color: "#7c3aed", bg: "#f5f3ff", border: "#c4b5fd", change: "Live", link: canView(userType, "units") ? "/dashboard/units" : null },
    { label: "Total Customers", value: s.customers ?? "—", icon: "👥", color: "#d97706", bg: "#fffbeb", border: "#fcd34d", change: `+${s.customers ?? 0}`, link: canView(userType, "customers") ? "/dashboard/customers" : null },
    { label: "Open Registry", value: s.openregistry ?? "—", icon: "📂", color: "#db2777", bg: "#fdf2f8", border: "#f9a8d4", change: "Pending", link: canView(userType, "registry") ? "/dashboard/registry" : null },
    { label: "Registry Done", value: s.doneregistry ?? "—", icon: "✅", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc", change: `${s.schregistry ?? 0} scheduled`, link: canView(userType, "registry") ? "/dashboard/registry" : null },
    { label: "Total Loans", value: s.loans ?? "—", icon: "🏦", color: "#ea580c", bg: "#fff7ed", border: "#fed7aa", change: `${s.appliedloan ?? 0} applied` },
    { label: "Overdue Tasks", value: s.overdueTask ?? "—", icon: "⚠️", color: "#dc2626", bg: "#fef2f2", border: "#fecaca", change: s.overdueTask > 0 ? "Urgent" : "Clear" },
  ];

  const revenueItems = [
    { label: "Total Confirmed Payments", value: s.cpayment ?? "—", icon: "✔️" },
    { label: "Rejected Payments", value: s.rpayment ?? "—", icon: "❌" },
    { label: "Open Loans", value: s.openloan ?? "—", icon: "🏦" },
    { label: "Sanctioned Loans", value: s.sanctionloan ?? "—", icon: "💎" },
  ];

  const taskItems = [
    { label: "Current Tasks", value: s.curentTask ?? 0, color: "#2563eb" },
    { label: "Completed Tasks", value: s.completedTask ?? 0, color: "#059669" },
    { label: "Overdue Tasks", value: s.overdueTask ?? 0, color: "#dc2626" },
    { label: "Demands Open", value: s.dopen ?? 0, color: "#d97706" },
    { label: "Demands Overdue", value: s.doverdue ?? 0, color: "#db2777" },
  ];

  const chequeItems = [
    { label: "Confirmed Cheques", value: s.ccheque ?? 0, color: "#059669" },
    { label: "Pending Cheques", value: s.pcheque ?? 0, color: "#d97706" },
    { label: "Returned Cheques", value: s.rcheque ?? 0, color: "#dc2626" },
  ];

  const bookingStatusBoxes = [
    { name: "Booked", count: bookingCounts["Booked"] ?? 0, icon: "📝", color: "#2563eb", bg: "#eff6ff" },
    { name: "Cancel", count: bookingCounts["Cancel"] ?? 0, icon: "❌", color: "#dc2626", bg: "#fef2f2" },
    { name: "Document Pending", count: bookingCounts["Document Pending"] ?? 0, icon: "📄", color: "#d97706", bg: "#fffbeb" },
    { name: "File Submit", count: bookingCounts["File Submit"] ?? 0, icon: "📁", color: "#7c3aed", bg: "#f5f3ff" },
    { name: "Loan Sanctioned", count: bookingCounts["Loan Sanctioned"] ?? 0, icon: "🏦", color: "#059669", bg: "#ecfdf5" },
    { name: "Registry Scheduled", count: bookingCounts["Registry Scheduled"] ?? 0, icon: "📅", color: "#0891b2", bg: "#ecfeff" },
    { name: "Registry Done", count: bookingCounts["Registry Done"] ?? 0, icon: "✅", color: "#10b981", bg: "#f0fdf4" },
    { name: "Construction Delay", count: bookingCounts["Construction Delay"] ?? 0, icon: "⏳", color: "#ea580c", bg: "#fff7ed" },
    { name: "Under Construction", count: bookingCounts["Under Construction"] ?? 0, icon: "🏗️", color: "#6366f1", bg: "#eef2ff" },
    { name: "Construction Complete", count: bookingCounts["Construction Complete"] ?? 0, icon: "🏠", color: "#8b5cf6", bg: "#f5f3ff" },
    { name: "Checklist Signed", count: bookingCounts["Checklist Signed"] ?? 0, icon: "✍️", color: "#db2777", bg: "#fdf2f8" },
    { name: "Possesion Letter Issued", count: bookingCounts["Possesion Letter Issued"] ?? 0, icon: "🔑", color: "#14b8a6", bg: "#f0fdfa" },
  ];

  return (
    <>
      <style>
        {`
        .rdb-home { display:flex; flex-direction:column; gap:24px; }

        .rdb-page-header { display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:12px; }
        .rdb-page-title { font-size:24px; font-weight:700; color:#0f172a; }
        .rdb-page-desc { font-size:14px; color:#64748b; margin-top:4px; }
        .rdb-date-badge { padding:8px 16px; background:#fff; border:1px solid #e2e8f0; border-radius:10px; font-size:13px; color:#64748b; font-weight:500; box-shadow:0 1px 3px rgba(0,0,0,.04); }

        /* Loading */
        .rdb-loading { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:14px; }
        .rdb-spinner { width:40px; height:40px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:rdb-sp .7s linear infinite; }
        @keyframes rdb-sp { to{transform:rotate(360deg)} }
        .rdb-load-t { font-size:14px; color:#64748b; }

        /* Stats Grid */
        .rdb-stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }

        .rdb-stat-card { background:#fff; border-radius:16px; padding:20px; border:1px solid #e2e8f0; transition:all .25s ease; cursor:pointer; position:relative; overflow:hidden; box-shadow:0 1px 3px rgba(0,0,0,.04); text-decoration:none; }
        .rdb-stat-card:hover { transform:translateY(-3px); box-shadow:0 8px 25px rgba(0,0,0,.08); border-color:#cbd5e1; }
        .rdb-stat-top { display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; }
        .rdb-stat-icon { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:20px; }
        .rdb-stat-change { font-size:11px; font-weight:600; padding:4px 10px; border-radius:20px; }
        .rdb-stat-value { font-size:28px; font-weight:800; color:#0f172a; margin-bottom:4px; letter-spacing:-.5px; }
        .rdb-stat-label { font-size:13px; color:#94a3b8; font-weight:500; }

        /* Cards Row */
        .rdb-cards-row { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
        .rdb-card { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:24px; box-shadow:0 1px 3px rgba(0,0,0,.04); }
        .rdb-card-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; }
        .rdb-card-title { font-size:16px; font-weight:600; color:#0f172a; }
        .rdb-card-badge { font-size:11px; padding:4px 12px; background:#eff6ff; color:#2563eb; border-radius:20px; font-weight:600; border:1px solid #bfdbfe; }

        /* Revenue Items */
        .rdb-revenue-list { display:flex; flex-direction:column; gap:10px; }
        .rdb-revenue-item { display:flex; justify-content:space-between; align-items:center; padding:14px 16px; background:#f8fafc; border-radius:12px; border:1px solid #f1f5f9; transition:all .2s; }
        .rdb-revenue-item:hover { background:#f1f5f9; border-color:#e2e8f0; }
        .rdb-revenue-left { display:flex; align-items:center; gap:12px; }
        .rdb-revenue-icon { font-size:18px; width:38px; height:38px; background:#eff6ff; border:1px solid #dbeafe; border-radius:10px; display:flex; align-items:center; justify-content:center; }
        .rdb-revenue-label { font-size:13px; color:#64748b; font-weight:500; }
        .rdb-revenue-value { font-size:18px; font-weight:700; color:#0f172a; }

        /* Task/Cheque Items */
        .rdb-task-list { display:flex; flex-direction:column; gap:8px; }
        .rdb-task-item { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; background:#f8fafc; border-radius:12px; border:1px solid #f1f5f9; transition:all .2s; }
        .rdb-task-item:hover { background:#f1f5f9; border-color:#e2e8f0; }
        .rdb-task-left { display:flex; align-items:center; gap:10px; }
        .rdb-task-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
        .rdb-task-label { font-size:13px; color:#64748b; font-weight:500; }
        .rdb-task-val { font-size:18px; font-weight:700; color:#0f172a; min-width:40px; text-align:right; }

        /* Insights */
        .rdb-insights-card { background:linear-gradient(135deg,#eff6ff,#eef2ff); border:1px solid #dbeafe; border-radius:16px; padding:24px; display:flex; align-items:center; gap:20px; }
        .rdb-insights-icon { font-size:40px; flex-shrink:0; }
        .rdb-insights-text h4 { font-size:15px; font-weight:600; color:#1e293b; margin-bottom:6px; }
        .rdb-insights-text p { font-size:13px; color:#64748b; line-height:1.6; }

        /* Responsive */
        @media(max-width:1200px){ .rdb-stats-grid{grid-template-columns:repeat(3,1fr);} }
        @media(max-width:900px){ .rdb-stats-grid{grid-template-columns:repeat(2,1fr);} .rdb-cards-row{grid-template-columns:1fr;} }
        @media(max-width:600px){
          .rdb-stats-grid{grid-template-columns:1fr 1fr;gap:10px;}
          .rdb-stat-card{padding:16px;} .rdb-stat-value{font-size:22px;} .rdb-stat-icon{width:38px;height:38px;font-size:16px;}
          .rdb-page-title{font-size:20px;} .rdb-card{padding:18px;}
          .rdb-insights-card{flex-direction:column;text-align:center;}
        }
        @media(max-width:400px){ .rdb-stats-grid{grid-template-columns:1fr;} }

        /* Booking Pipeline Grid */
        .rdb-status-grid { display:grid; grid-template-columns:repeat(6,1fr); gap:12px; }
        .rdb-status-box { background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:16px; display:flex; flex-direction:column; align-items:center; gap:8px; cursor:pointer; transition:all .2s; }
        .rdb-status-box:hover { transform:translateY(-2px); box-shadow:0 4px 12px rgba(0,0,0,.05); border-color:#cbd5e1; }
        .rdb-status-icon { font-size:20px; }
        .rdb-status-count { font-size:20px; font-weight:700; color:#0f172a; }
        .rdb-status-name { font-size:11px; font-weight:600; color:#64748b; text-align:center; text-transform:uppercase; letter-spacing:.3px; }

        @media(max-width:1200px){ .rdb-status-grid{grid-template-columns:repeat(4,1fr);} }
        @media(max-width:800px){ .rdb-status-grid{grid-template-columns:repeat(3,1fr);} }
        @media(max-width:500px){ .rdb-status-grid{grid-template-columns:repeat(2,1fr);} }
        `}
      </style>

      <div className="rdb-home">
        {/* Page Header */}
        <div className="rdb-page-header">
          <div>
            <h1 className="rdb-page-title">Dashboard</h1>
            <p className="rdb-page-desc">Welcome back! Here's your real estate overview</p>
          </div>
          <div className="rdb-date-badge">
            📅 {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>

        {loading ? (
          <div className="rdb-loading">
            <div className="rdb-spinner" />
            <div className="rdb-load-t">Loading dashboard...</div>
          </div>
        ) : (
          <>
            {/* Booking Pipeline - Moved to Top */}
            <div className="rdb-card" style={{ marginBottom: "20px" }}>
              <div className="rdb-card-header">
                <span className="rdb-card-title">🏗️ Booking Pipeline</span>
                <span className="rdb-card-badge">Status Highlights</span>
              </div>
              <div className="rdb-status-grid">
                {bookingStatusBoxes.map((box, i) => (
                  <div key={i} className="rdb-status-box" onClick={() => navigate("/dashboard/bookings", { state: { selectedStatus: box.name } })}>
                    <span className="rdb-status-icon" style={{ background: box.bg, padding: "8px", borderRadius: "10px" }}>{box.icon}</span>
                    <span className="rdb-status-count">{box.count}</span>
                    <span className="rdb-status-name">{box.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="rdb-stats-grid">
              {statCards.map((c, i) => (
                <div key={i} className="rdb-stat-card" onClick={() => c.link && navigate(c.link)}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: `linear-gradient(90deg,${c.color},transparent)`, borderRadius: "16px 16px 0 0" }} />
                  <div className="rdb-stat-top">
                    <div className="rdb-stat-icon" style={{ background: c.bg, border: `1px solid ${c.border}` }}>{c.icon}</div>
                    <span className="rdb-stat-change" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>{c.change}</span>
                  </div>
                  <div className="rdb-stat-value">{c.value}</div>
                  <div className="rdb-stat-label">{c.label}</div>
                </div>
              ))}
            </div>

            {/* Overview & Tasks Row */}
            <div className="rdb-cards-row">
              {/* Payment & Loan Overview */}
              <div className="rdb-card">
                <div className="rdb-card-header">
                  <span className="rdb-card-title">💎 Payments & Loans</span>
                  <span className="rdb-card-badge">Overview</span>
                </div>
                <div className="rdb-revenue-list">
                  {revenueItems.map((item, i) => (
                    <div key={i} className="rdb-revenue-item">
                      <div className="rdb-revenue-left">
                        <span className="rdb-revenue-icon">{item.icon}</span>
                        <span className="rdb-revenue-label">{item.label}</span>
                      </div>
                      <span className="rdb-revenue-value">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tasks */}
              <div className="rdb-card">
                <div className="rdb-card-header">
                  <span className="rdb-card-title">⚡ Tasks & Demands</span>
                  <span className="rdb-card-badge">Status</span>
                </div>
                <div className="rdb-task-list">
                  {taskItems.map((t, i) => (
                    <div key={i} className="rdb-task-item">
                      <div className="rdb-task-left">
                        <div className="rdb-task-dot" style={{ background: t.color }} />
                        <span className="rdb-task-label">{t.label}</span>
                      </div>
                      <span className="rdb-task-val">{t.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>



            {/* Cheques & Quick Summary */}
            <div className="rdb-cards-row">
              {/* Cheques */}
              <div className="rdb-card">
                <div className="rdb-card-header">
                  <span className="rdb-card-title">🏦 Cheque Status</span>
                  <span className="rdb-card-badge">All Time</span>
                </div>
                <div className="rdb-task-list">
                  {chequeItems.map((c, i) => (
                    <div key={i} className="rdb-task-item">
                      <div className="rdb-task-left">
                        <div className="rdb-task-dot" style={{ background: c.color }} />
                        <span className="rdb-task-label">{c.label}</span>
                      </div>
                      <span className="rdb-task-val">{c.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="rdb-card">
                <div className="rdb-card-header">
                  <span className="rdb-card-title">📊 Registry Summary</span>
                  <span className="rdb-card-badge">Status</span>
                </div>
                <div className="rdb-task-list">
                  <div className="rdb-task-item">
                    <div className="rdb-task-left">
                      <div className="rdb-task-dot" style={{ background: "#d97706" }} />
                      <span className="rdb-task-label">Open Registry</span>
                    </div>
                    <span className="rdb-task-val">{s.openregistry ?? 0}</span>
                  </div>
                  <div className="rdb-task-item">
                    <div className="rdb-task-left">
                      <div className="rdb-task-dot" style={{ background: "#2563eb" }} />
                      <span className="rdb-task-label">Scheduled Registry</span>
                    </div>
                    <span className="rdb-task-val">{s.schregistry ?? 0}</span>
                  </div>
                  <div className="rdb-task-item">
                    <div className="rdb-task-left">
                      <div className="rdb-task-dot" style={{ background: "#059669" }} />
                      <span className="rdb-task-label">Completed Registry</span>
                    </div>
                    <span className="rdb-task-val">{s.doneregistry ?? 0}</span>
                  </div>
                  <div className="rdb-task-item">
                    <div className="rdb-task-left">
                      <div className="rdb-task-dot" style={{ background: "#dc2626" }} />
                      <span className="rdb-task-label">Cancelled Bookings</span>
                    </div>
                    <span className="rdb-task-val">{s.cancelbookings ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Insights */}
            <div className="rdb-insights-card">
              <span className="rdb-insights-icon">📈</span>
              <div className="rdb-insights-text">
                <h4>Platform Overview</h4>
                <p>
                  You have <strong>{s.bookin ?? 0}</strong> bookings across <strong>{s.units ?? 0}</strong> units
                  with <strong>{s.customers ?? 0}</strong> registered customers.
                  {" "}<strong>{s.cpayment ?? 0}</strong> payments confirmed out of <strong>{s.payments ?? 0}</strong> total.
                  {" "}<strong>{s.appliedloan ?? 0}</strong> loan applications with <strong>{s.sanctionloan ?? 0}</strong> sanctioned.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DashboardHome;
